import { useEffect, useRef, type MouseEvent } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ServiceScreen from './components/ServiceScreen';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import useLightReveal from '@/hooks/useLightReveal';
import { services, expertiseHeroImg } from '@/mocks/expertise';

gsap.registerPlugin(ScrollTrigger);

const STICKY_MQ = '(min-width: 1024px) and (min-height: 600px) and (prefers-reduced-motion: no-preference)';

export default function ExpertisePage() {
  const ref = useRef<HTMLElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  useLightReveal(ref, 'expertise');

  // Codrops StickySections demo 10, adapted: sticky stage + scrubbed yPercent, native scroll
  useEffect(() => {
    const stack = stackRef.current;
    if (!stack) return undefined;
    const mm = gsap.matchMedia();
    mm.add(STICKY_MQ, () => {
      const panels = gsap.utils.toArray<HTMLElement>(stack.querySelectorAll('.exp-stage > section'));
      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: { trigger: stack, start: 'top 56px', end: 'bottom bottom', scrub: true, invalidateOnRefresh: true },
      });
      const dims = panels.map((p) => p.querySelector('.exp-dim')).filter(Boolean) as HTMLElement[];
      panels.slice(0, -1).forEach((p, i) => {
        tl.to(p, { yPercent: -100 }).to(dims[i], { opacity: 0.85 }, '<');
      });
      return () => {
        gsap.set(panels, { clearProps: 'transform' });
        gsap.set(dims, { clearProps: 'opacity' });
      };
    });
    const refresh = () => ScrollTrigger.refresh();
    const imgs = Array.from(document.querySelectorAll('main img')) as HTMLImageElement[];
    imgs.forEach((im) => { if (!im.complete) im.addEventListener('load', refresh, { once: true }); });
    window.addEventListener('load', refresh);
    return () => {
      imgs.forEach((im) => im.removeEventListener('load', refresh));
      window.removeEventListener('load', refresh);
      mm.revert();
    };
  }, []);

  const scrollToService = (id: string) => {
    const stack = stackRef.current;
    const idx = services.findIndex((s) => s.id === id);
    if (stack && idx >= 0 && window.matchMedia(STICKY_MQ).matches) {
      const panelH = window.innerHeight - 56;
      const top = stack.getBoundingClientRect().top + window.scrollY - 56 + idx * panelH;
      window.scrollTo({ top: Math.round(top) + (idx > 0 ? 1 : 0) });
    } else {
      document.getElementById(id)?.scrollIntoView({ block: 'start' });
    }
  };

  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id || !services.some((s) => s.id === id)) return undefined;
    const t = window.setTimeout(() => scrollToService(id), 120);
    return () => window.clearTimeout(t);
  }, []);

  const jump = (id: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.history.pushState(null, '', `#${id}`);
    scrollToService(id);
  };

  return (
    <>
      <title>Expertise | AUREN Management Consulting — Strategy, Transformation, Leadership</title>
      <meta name="description" content="AUREN helps organizations turn complex decisions into clear direction across strategy and growth, transformation, and leadership and change." />
      <Navbar />
      <main ref={ref} className="bg-background-50 pt-14">
        <section className="wrap pt-20 md:pt-32 pb-12 md:pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-8 items-end">
            <div className="lg:col-span-8" data-fade>
              <span className="label-xs text-foreground-600">What we do / AUREN</span>
              <h1 className="font-heading font-light uppercase leading-[0.92] tracking-[0.02em] text-foreground-950 mt-6 text-[clamp(44px,7vw,100px)]">Expertise.</h1>
            </div>
            <p className="lg:col-span-4 text-[15px] md:text-[16px] leading-[1.6] text-foreground-700 max-w-[360px] lg:pb-3" data-fade>
              We help organizations turn complex decisions into clear direction.
            </p>
          </div>
        </section>

        <section className="relative w-full min-h-[640px] md:h-[90vh] overflow-hidden bg-foreground-950" aria-label="Service areas" data-route-dark>
          <img src={expertiseHeroImg} alt="Aerial view of modern architecture in deep blue tones" title="AUREN expertise" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-secondary-950/40" />
          <div className="relative grid grid-cols-1 md:grid-cols-3 h-full min-h-[640px] md:min-h-0">
            {services.map((s, i) => (
              <a key={s.id} href={`#${s.id}`} onClick={jump(s.id)} className={`group relative flex flex-col items-center justify-center text-center px-6 py-16 cursor-pointer text-white transition-colors duration-500 hover:bg-white/[0.06] focus-visible:bg-white/[0.08] outline-none ${i > 0 ? 'border-t md:border-t-0 md:border-l border-white/40' : ''}`}>
                <span className="label-xs text-white/70">{s.no}</span>
                <span className="font-heading font-light uppercase tracking-[0.06em] text-[20px] md:text-[24px] lg:text-[28px] mt-4">{s.title}</span>
                <span className="mt-6 w-11 h-11 rounded-full border border-white/60 flex items-center justify-center opacity-70 group-hover:opacity-100 group-hover:bg-white group-hover:text-foreground-950 transition-[opacity,background-color,color,border-color] duration-300">
                  <i className="ri-arrow-down-line transition-transform duration-300 group-hover:translate-y-0.5" />
                </span>
              </a>
            ))}
          </div>
        </section>

        <div ref={stackRef} className="exp-stack">
          <div className="exp-stage">
            {services.map((s) => (
              <ServiceScreen key={s.id} service={s} total={services.length} />
            ))}
          </div>
        </div>

        <section className="border-t border-foreground-950/15">
          <div className="wrap py-20 md:py-28 flex flex-col md:flex-row md:items-end justify-between gap-10" data-fade>
            <p className="font-heading font-light uppercase tracking-[0.02em] leading-[1.02] text-foreground-950 text-[clamp(32px,5vw,72px)]">Clearer thinking.<br />Better decisions.</p>
            <Link to="/contact" className="group inline-flex items-center gap-4 label-xs text-foreground-950 cursor-pointer whitespace-nowrap">
              Start a conversation<span className="circle-arrow inv-dark"><i className="ri-arrow-right-line" /></span>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}